import React from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { FontFamily } from '../../../../../constants/fonts';
import TablePager from '../../CrewingAnalytics/components/TablePager';
import type { WesDetailRowView } from '../wesPalletStagingMappers';
import { isWesApiSortable } from '../wesPalletStagingRequest';
import { isDimensionKey, WES_DIMENSIONS, WES_METRICS, WES_PAGE_SIZE_OPTIONS, WES_TABLE_EMPTY_MESSAGE, effectiveSort, type WesDimKey, type WesTableSort } from '../wesPalletStagingTable';
import { WesTooltipTarget } from './WesTooltip';
import WesPressable from './WesPressable';

const DIM_WIDTH = 140;
const METRIC_WIDTH = 150;

// Columns grow to share the full width and keep a minimum; the table scrolls sideways only when the minimums do not fit.
const grow = (min: number, factor = 1) => ({ flexGrow: factor, flexShrink: 0, flexBasis: min * factor, minWidth: min * factor });

interface WesLocationTableProps {
  // The current page, already grouped, sorted and totalled by the API.
  rows: WesDetailRowView[];
  totals: WesDetailRowView;
  dimensions: WesDimKey[];
  sort: WesTableSort | null;
  page: number;
  totalGroups: number;
  pageSize: number;
  empty: boolean;
  onSort: (key: string) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

// Look follows the Crewing Location Level Efficiency table; the Total row stays pinned under the header. Rows arrive already sorted across all pages.
const WesLocationTable: React.FC<WesLocationTableProps> = ({ rows, totals, dimensions, sort, page, totalGroups, pageSize, empty, onSort, onPageChange, onPageSizeChange }) => {
  const dims = WES_DIMENSIONS.filter((d) => dimensions.includes(d.key));
  const active = effectiveSort(sort, dimensions);

  const header = (key: string, label: string, width: number, numeric: boolean) => {
    const sortable = isWesApiSortable(key) || isDimensionKey(key);
    const isActive = sortable && sort !== null && active.key === key;
    const content = (hovered: boolean) => (
      <>
        <Text style={[styles.headerText, isActive && styles.headerTextActive, numeric && styles.headerTextNum, hovered && styles.headerTextHover]} numberOfLines={2}>{label}</Text>
        {sortable ? <Text style={styles.sortMark} testID={`wes-sort-${key}`}>{isActive ? (active.dir === 'asc' ? '▲' : '▼') : ''}</Text> : null}
      </>
    );
    return sortable ? (
      <WesPressable
        key={key}
        style={[styles.headerCell, numeric && styles.headerCellNum, grow(width)]}
        onPress={() => onSort(key)}
        accessibilityRole="button"
        accessibilityLabel={`Sort by ${label}`}
        testID={`wes-th-${key}`}
      >
        {({ hovered }) => content(hovered)}
      </WesPressable>
    ) : (
      <View key={key} style={[styles.headerCell, numeric && styles.headerCellNum, grow(width)]} testID={`wes-th-${key}`}>
        {content(false)}
      </View>
    );
  };

  const metricCell = (row: WesDetailRowView, key: (typeof WES_METRICS)[number]['key'], cellTestID: string, tipTestID: string, textStyle: object) => {
    if (key !== 'audit') {
      return (
        <Text key={key} style={[textStyle, styles.num, grow(METRIC_WIDTH)]} testID={cellTestID}>
          {row.metrics[key]}
        </Text>
      );
    }
    return (
      <View key={key} style={[grow(METRIC_WIDTH), styles.auditCell]}>
        <WesTooltipTarget text={row.auditTip} testID={tipTestID} placement="left" maxWidth={420}>
          <Text style={[textStyle, styles.num]} testID={cellTestID}>
            {row.metrics[key]}
          </Text>
        </WesTooltipTarget>
      </View>
    );
  };

  return (
    <View>
      <ScrollView horizontal showsHorizontalScrollIndicator contentContainerStyle={styles.scrollContent}>
        <View style={styles.tableInner}>
          <View style={styles.headerRow} testID="wes-header-row">
            {dims.map((d) => header(d.key, d.label, DIM_WIDTH, false))}
            {WES_METRICS.map((m) => header(m.key, m.label, METRIC_WIDTH, true))}
          </View>

          {empty ? (
            <View style={styles.emptyRow} testID="wes-table-empty">
              <Text style={styles.emptyText}>{WES_TABLE_EMPTY_MESSAGE}</Text>
            </View>
          ) : (
            <>
              <View style={styles.totalRow} testID="wes-total-row">
                <View style={grow(DIM_WIDTH, dims.length)}>
                  <Text style={[styles.totalText, styles.totalLabel]}>Total</Text>
                </View>
                {WES_METRICS.map((m) => metricCell(totals, m.key, `wes-total-${m.key}`, 'wes-tip-total-audit', styles.totalText))}
              </View>

              {rows.map((row, ri) => (
                <WesPressable key={row.id} style={styles.row} hoverStyle={styles.rowHover} testID={`wes-row-${ri}`}>
                  {dims.map((d) => {
                    const value = row.text[d.key] ?? '';
                    const cell = (
                      <Text style={styles.cell} numberOfLines={1} testID={`wes-cell-${ri}-${d.key}`}>
                        {value}
                      </Text>
                    );
                    return d.key === 'location' ? (
                      <WesTooltipTarget key={d.key} text={value} testID={`wes-location-name-${ri}`} style={grow(DIM_WIDTH)} maxWidth={300}>
                        {cell}
                      </WesTooltipTarget>
                    ) : (
                      <View key={d.key} style={grow(DIM_WIDTH)}>
                        {cell}
                      </View>
                    );
                  })}
                  {WES_METRICS.map((m) => metricCell(row, m.key, `wes-cell-${ri}-${m.key}`, `wes-tip-${ri}-audit`, styles.cell))}
                </WesPressable>
              ))}
            </>
          )}
        </View>
      </ScrollView>

      {empty ? null : <TablePager page={page} pageSize={pageSize} total={totalGroups} onPageChange={onPageChange} pageSizeOptions={WES_PAGE_SIZE_OPTIONS} onPageSizeChange={onPageSizeChange} hoverEffects />}
    </View>
  );
};

const styles = StyleSheet.create({
  scrollContent: { flexGrow: 1 },
  tableInner: { flexGrow: 1, minWidth: '100%' },
  headerRow: { flexDirection: 'row', alignItems: 'stretch', borderBottomWidth: 2, borderBottomColor: '#D7E5F8', paddingVertical: 8, backgroundColor: '#FFFFFF' },
  headerCell: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', paddingHorizontal: 6 },
  headerCellNum: { justifyContent: 'flex-end' },
  headerText: { flexShrink: 1, fontSize: 10, fontFamily: FontFamily.bold, fontWeight: '700', color: '#616161', textTransform: 'uppercase', letterSpacing: 0.3 },
  headerTextNum: { textAlign: 'right' },
  headerTextActive: { color: '#034895' },
  headerTextHover: { color: '#005CBC' },
  sortMark: { fontSize: 10, color: '#005CBC', marginLeft: 2, minWidth: 10 },
  row: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#D7E5F8', paddingVertical: 8, cursor: 'auto' },
  rowHover: { backgroundColor: '#E7EFFB' },
  emptyRow: { minHeight: 64, justifyContent: 'center', alignItems: 'center', borderBottomWidth: 1, borderBottomColor: '#D7E5F8' },
  emptyText: { paddingHorizontal: 12, fontSize: 13, fontFamily: FontFamily.medium, color: '#616161', textAlign: 'center' },
  totalRow: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: '#D0D0D0',
    borderBottomWidth: 2,
    borderBottomColor: '#D7E5F8',
    backgroundColor: '#F3F7FD',
    paddingVertical: 9,
  },
  totalLabel: { paddingHorizontal: 6 },
  totalText: { fontSize: 12, fontFamily: FontFamily.bold, fontWeight: '700', color: '#2D2D2D', paddingHorizontal: 6 },
  cell: { paddingHorizontal: 6, fontSize: 12, fontFamily: FontFamily.medium, color: '#3A3A3A' },
  num: { textAlign: 'right' },
  auditCell: { alignItems: 'flex-end' },
});

export default WesLocationTable;
